<template>
  <div class="sketch-trim-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="修剪模式" name="mode">
        <n-radio-group
          :value="trimOptions.mode"
          size="small"
          @update:value="handleModeChange"
        >
          <div class="mode-list">
            <n-radio :value="SketchTrimMode.Power">强力剪裁（Power Trim）</n-radio>
            <n-radio :value="SketchTrimMode.Corner">角点（Corner）</n-radio>
            <n-radio :value="SketchTrimMode.TrimAwayInside">在内剪除（Trim Away Inside）</n-radio>
            <n-radio :value="SketchTrimMode.TrimAwayOutside">在外剪除（Trim Away Outside）</n-radio>
            <n-radio :value="SketchTrimMode.TrimToClosest">剪裁到最近端（Trim to Closest）</n-radio>
          </div>
        </n-radio-group>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <div class="option-list">
          <n-checkbox
            :checked="trimOptions.keepTrimmedAsConstruction"
            @update:checked="handleKeepTrimmedAsConstruction"
          >
            将修剪段保留为构造几何
          </n-checkbox>
          <n-checkbox
            :checked="trimOptions.ignoreConstructionGeometry"
            @update:checked="handleIgnoreConstructionGeometry"
          >
            忽略构造几何体的修剪
          </n-checkbox>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NRadio,
  NRadioGroup,
} from 'naive-ui';
import type {
  PropertyManagerContext,
  SketchTrimMode as SketchTrimModeType,
  SketchTrimOptions,
} from '@/core';
import {SketchTrimMode, SketchTrimSingleEntityBehavior} from '@/core';

/**
 * 草图修剪属性面板组件属性。
 */
interface SketchTrimSectionProps {
  /** 属性面板上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<SketchTrimSectionProps>();

/**
 * 剪裁选项默认值。
 */
const DEFAULT_TRIM_OPTIONS: SketchTrimOptions = {
  mode: SketchTrimMode.Power,
  singleEntityBehavior: SketchTrimSingleEntityBehavior.Trim,
  keepTrimmedAsConstruction: false,
  ignoreConstructionGeometry: false,
};

/**
 * 默认展开分组。
 */
const defaultExpanded = ['mode', 'options'];

/**
 * 当前剪裁选项快照。
 */
const trimOptions = computed<SketchTrimOptions>(() => {
  return props.context.sketchTrimOptions ?? DEFAULT_TRIM_OPTIONS;
});

/**
 * 提交剪裁选项。
 *
 * @param nextOptions 新选项。
 */
function commitTrimOptions(nextOptions: SketchTrimOptions): void {
  props.context.setSketchTrimOptions?.(nextOptions);
}

/**
 * 处理模式切换。
 *
 * @param value 新模式。
 */
function handleModeChange(value: string | number): void {
  const mode = value as SketchTrimModeType;
  commitTrimOptions({
    ...trimOptions.value,
    mode,
  });
}

/**
 * 处理“保留构造几何”切换。
 *
 * @param checked 是否勾选。
 */
function handleKeepTrimmedAsConstruction(checked: boolean): void {
  commitTrimOptions({
    ...trimOptions.value,
    keepTrimmedAsConstruction: checked,
  });
}

/**
 * 处理“忽略构造几何”切换。
 *
 * @param checked 是否勾选。
 */
function handleIgnoreConstructionGeometry(checked: boolean): void {
  commitTrimOptions({
    ...trimOptions.value,
    ignoreConstructionGeometry: checked,
  });
}
</script>

<style scoped>
.sketch-trim-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mode-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
